import { Stack, Text } from '@mantine/core'
import React, { useMemo } from 'react'
import { useTheme } from '../../../components/ThemeProvider'

interface FocusModulesProps {
  data: any
}

interface FocusDetail {
  module_id: string
  module_name: string
  reasons: string[]
}

interface ModuleItem {
  key: string
  name: string
  reasons?: string[]
}

export default function FocusModules({ data }: FocusModulesProps) {
  const theme = useTheme()

  const focusModules = useMemo(() => {
    const focusDetails: FocusDetail[] = data?.meta_index_focus_details ?? []
    const focusMap = new Map<string, FocusDetail>()
    focusDetails.forEach((detail) => {
      focusMap.set(detail.module_id, detail)
    })

    const focusSet = new Set<string>(data?.meta_index_focus_list ?? [])
    focusDetails.forEach((detail) => focusSet.add(detail.module_id))

    return (data?.meta_data_module_list ?? [])
      .map((item: any) => {
        const entries = Object.entries(item)
        if (!entries.length) return null
        const [key, value] = entries[0] as [string, any]
        const focusDetail = focusMap.get(key)
        if (!focusSet.has(key)) return null
        return {
          key,
          name: value as string,
          reasons: focusDetail?.reasons,
        }
      })
      .filter(Boolean) as ModuleItem[]
  }, [data])

  if (!focusModules.length) {
    return null
  }

  return (
    <div className="space-y-4">
      <Text size="sm" fw={600} style={{ color: theme.accentTextColor }}>
        关注模块
      </Text>
      <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
        {focusModules.map((item, index) => (
          <div
            key={item.key}
            className="rounded-lg border px-4 py-4"
            style={{
              backgroundColor: `${theme.warningColor}08`,
              borderColor: `${theme.warningColor}28`,
            }}
          >
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Text size="xs" fw={600} style={{ color: theme.accentTextColor }}>
                    {String(index + 1).padStart(2, '0')}
                  </Text>
                  <Text size="sm" fw={600} className="text-gray-800">
                    {item.name}
                  </Text>
                  <span
                    className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    style={{
                      backgroundColor: `${theme.warningColor}15`,
                      color: theme.warningColor,
                    }}
                  >
                    关注
                  </span>
                </div>
                {item.reasons && item.reasons.length > 0 && (
                  <Stack gap="xs" className="mt-3">
                    {item.reasons.map((reason, idx) => (
                      <Text size="xs" key={idx} className="text-gray-700" style={{ lineHeight: 1.5 }}>
                        {reason}
                      </Text>
                    ))}
                  </Stack>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
